import { useState, useEffect, useCallback } from 'react'
import { Check, Share2 } from 'lucide-react'
import { AudioNote } from '@highlight/common'
import { useGenerateAudioShareLink } from '@renderer/hooks/share-link'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { getChatApiUrl } from '@shared/utils/ChatBackendUtils'

export function useMeetingNoteShare(audioNote: AudioNote | undefined) {
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { mutateAsync: generateShareLink, isPending } = useGenerateAudioShareLink()

  const handleShare = useCallback(async () => {
    if (!audioNote) return

    const isStagingBackend = getChatApiUrl().includes('staging')
    let linkToCopy = audioNote.shareLink

    // generateShareLink rejects on failure, so confirming the copy has to wait
    // until it resolves — otherwise a failed link both escapes as an unhandled
    // rejection and flashes "Copied!" for a link that was never created.
    try {
      if (linkToCopy) {
        // Copy existing link with staging param
        if (isStagingBackend && !linkToCopy.includes('env=staging')) {
          linkToCopy = `${linkToCopy}?env=staging`
        }
        await OverlayIpcRenderer.writeTextToClipboard(linkToCopy)

        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'copy link',
          trigger: 'sidepanel',
        })
      } else {
        // Generate new link (hook handles clipboard, IPC, tracking)
        await generateShareLink({ audioNote })
      }
    } catch (err) {
      console.error('Failed to share meeting note', err)
      setError('Link could not be created')
      return
    }

    setError(null)
    setCopied(true)
  }, [audioNote, generateShareLink])

  // Clear a stale error once the user tries again.
  useEffect(() => {
    if (isPending) setError(null)
  }, [isPending])

  // Auto-reset after 2 seconds
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  return {
    handleShare,
    isLoading: isPending,
    copied,
    error,
    icon: copied ? Check : Share2,
    tooltip: error ?? (copied ? 'Copied!' : isPending ? 'Getting link...' : 'Copy share link'),
  }
}
